import { Box, FormLabel, TextField } from "@mui/material";
import { ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import AutoCompleteSelect from "../../../../../components/autocomplete/autoCompleteSelect/AutoCompleteSelect";
import { useNewOrderQueryData } from "../../../../../hooks/useNewOrderQueryData";
import { InboundDeliveryDocumentType } from "../../../../../models/autogenerated-deliveryService-gql/graphql";
import { IGeneralInfo } from "../../../../../models/generalInfo";
import { isNullOrEmpty, isValidString } from "../../../../../utils/helper";
import "./GeneralInfoForm.scss";

interface GeneralInfoFormProps {
  formData: IGeneralInfo;
  onChange: (fieldID: string, text: string) => void;
  setIsFormValid: (isValid: boolean) => void;
}

const requiredFields: (keyof IGeneralInfo)[] = [
  "deliveryNumber",
  "documentType",
  "location",
];

const GeneralInfoForm = (props: GeneralInfoFormProps) => {
  const { t } = useTranslation();
  const { id: idFromUrl } = useParams();
  const { queryData } = useNewOrderQueryData();

  const { formData, onChange, setIsFormValid } = props;
  const isEditOrder = !!idFromUrl;

  const locationOptions = queryData.locations
    ?.map((l) => l.name)
    .filter(isValidString);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    onChange(id, value);
  };

  useEffect(() => {
    const isFormValid = requiredFields.every(
      (field) => !isNullOrEmpty(formData[field]),
    );

    setIsFormValid(isFormValid);
  }, [formData]);

  const renderTextField = (
    label: keyof IGeneralInfo,
    required?: boolean,
    disabled?: boolean,
  ) => (
    <>
      <FormLabel required={required}>{t(label)}</FormLabel>
      <TextField
        disabled={disabled}
        data-testid={`generalInfo-form-textfield-${label}`}
        id={label}
        className="input-element"
        value={formData[label]}
        onChange={handleChange}
        variant="outlined"
        placeholder={t(label + "Placeholder")}
        required={required}
      />
    </>
  );

  const renderAutoCompleteSelect = (
    label: keyof IGeneralInfo,
    options: string[],
    required?: boolean,
    disabled?: boolean,
  ) => (
    <AutoCompleteSelect
      fieldId={label}
      options={options}
      onSelect={onChange}
      initialValue={formData[label]}
      required={required}
      disabled={disabled}
    />
  );

  return (
    <Box
      className="form-flex-container"
      component="form"
      sx={{ width: "100%" }}
      autoComplete="off"
    >
      {renderTextField("deliveryNumber", true, isEditOrder)}
      {renderAutoCompleteSelect(
        "documentType",
        Object.values(InboundDeliveryDocumentType),
        true,
        isEditOrder,
      )}
      {renderAutoCompleteSelect(
        "location",
        locationOptions || [],
        true,
        isEditOrder,
      )}
      {renderTextField("documentNumber")}
      {renderTextField("deliveryNote")}
    </Box>
  );
};
export default GeneralInfoForm;
